import React from 'react';
import { motion } from 'framer-motion';

/**
 * SlideSummary Component
 * Final slide with key takeaways and call-to-action
 * 
 * Features:
 * - Key takeaways (numbered list)
 * - Call-to-action
 * - Contact information
 * - Clara360 branding
 */
const SlideSummary = ({ title, takeaways = [], cta, contact }) => {
  return (
    <div className="relative w-full h-full flex flex-col bg-gradient-to-br from-purple-50 via-white to-indigo-50 dark:from-gray-900 dark:to-purple-900/20 p-12 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-600 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-1 h-12 bg-gradient-to-b from-purple-600 to-indigo-600 rounded-full"></div>
            <h2 className="text-5xl font-bold text-gray-800 dark:text-white">
              {title || 'Zusammenfassung'}
            </h2>
          </div>
        </motion.div>

        {/* Key Takeaways */}
        <div className="flex-1 mb-12">
          {takeaways.length > 0 ? (
            <div className="space-y-6">
              {takeaways.map((takeaway, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
                  className="flex items-start space-x-4 group"
                >
                  {/* Number Badge */}
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 text-white flex items-center justify-center text-xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                    {index + 1}
                  </div>

                  {/* Takeaway Text */}
                  <div className="flex-1 pt-2">
                    <p className="text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                      {takeaway}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center text-gray-500 dark:text-gray-400"
            >
              <p className="text-xl">Keine Zusammenfassung verfügbar</p>
            </motion.div>
          )}
        </div>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="h-px bg-gradient-to-r from-transparent via-purple-300 to-transparent mb-12"
        ></motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mb-8"
        >
          {cta ? (
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-gray-800 dark:text-white">
                {cta.title || 'Haben Sie Fragen?'}
              </h3>
              {cta.description && (
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  {cta.description}
                </p>
              )}
              {cta.button && (
                <button className="mt-4 px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                  {cta.button}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold text-gray-800 dark:text-white">
                Vielen Dank für Ihre Aufmerksamkeit
              </h3>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Bei Fragen stehen wir Ihnen gerne zur Verfügung
              </p>
            </div>
          )}
        </motion.div>

        {/* Contact Info */}
        {contact && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="text-center text-sm text-gray-600 dark:text-gray-400 space-y-1"
          >
            {contact.email && (
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>{contact.email}</span>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>{contact.phone}</span>
              </div>
            )}
            {contact.website && (
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <span>{contact.website}</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Clara360 Branding */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="mt-auto pt-8 flex items-center justify-center space-x-3"
        >
          <div className="flex items-center space-x-2 px-4 py-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-full border border-purple-200 dark:border-purple-800">
            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
            </svg>
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Powered by Clara360 AI
            </span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SlideSummary;

